import { useState, useEffect, useRef } from 'react';
import type { PageData, BlockData, PageType } from '../types/editing';

export interface ActiveSpread {
  leftPageIndex: number | null;
  rightPageIndex: number | null;
  leftPageData: PageData | null;
  rightPageData: PageData | null;
  leftHasChanges: boolean;
  rightHasChanges: boolean;
}

interface UseSpreadStateProps {
  currentLeftPage: number;
  currentRightPage: number;
  pages: PageData[];
}

export const useSpreadState = ({ currentLeftPage, currentRightPage, pages }: UseSpreadStateProps) => {
  const [activeSpread, setActiveSpread] = useState<ActiveSpread>({
    leftPageIndex: null,
    rightPageIndex: null,
    leftPageData: null,
    rightPageData: null,
    leftHasChanges: false,
    rightHasChanges: false,
  });

  const prevSpreadRef = useRef<{ left: number; right: number } | null>(null);

  // Detect spread changes and update activeSpread
  useEffect(() => {
    const leftChanged = prevSpreadRef.current?.left !== currentLeftPage;
    const rightChanged = prevSpreadRef.current?.right !== currentRightPage;
    
    // Find page data from pages array
    const leftPageData = pages.find(page => page['page-index'] === currentLeftPage) || null;
    const rightPageData = pages.find(page => page['page-index'] === currentRightPage) || null;
    
    if (leftChanged || rightChanged || !prevSpreadRef.current) {
      // Update activeSpread
      setActiveSpread(prev => ({
        ...prev,
        leftPageIndex: currentLeftPage > 0 ? currentLeftPage : null,
        rightPageIndex: currentRightPage > 0 ? currentRightPage : null,
        leftPageData,
        rightPageData,
        // Reset change flags when spread changes
        leftHasChanges: false,
        rightHasChanges: false,
      }));

      prevSpreadRef.current = { left: currentLeftPage, right: currentRightPage };
    } else {
      // Spread hasn't changed, but pages array might have (e.g., pageType change)
      // Update leftPageData and rightPageData to reflect changes from pages prop
      // Note: Change detection (leftHasChanges/rightHasChanges) is handled in SpreadEditor
      // via checkPageHasChanges function, not here
      setActiveSpread(prev => {
        let updated = { ...prev };
        let hasUpdates = false;

        // Update leftPageData if it changed (for other uses like getWordDataStrategy)
        if (leftPageData && prev.leftPageIndex !== null) {
          updated.leftPageData = leftPageData;
          hasUpdates = true;
        }

        // Update rightPageData if it changed (for other uses like getWordDataStrategy)
        if (rightPageData && prev.rightPageIndex !== null) {
          updated.rightPageData = rightPageData;
          hasUpdates = true;
        }

        return hasUpdates ? updated : prev;
      });
    }
  }, [currentLeftPage, currentRightPage, pages]);

  return {
    activeSpread,
    setActiveSpread,
  };
};

